import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Package, Truck, CheckCircle, Euro, MapPin, ArrowRight, Calendar, Car, Plus, Route } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { parcelsApi, tripsApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate, formatPrice, getStatusLabel, getStatusColor } from '../../lib/utils';
import { toast } from 'sonner';

const CarrierDashboard = () => {
  const { user } = useAuth();
  const [availableParcels, setAvailableParcels] = useState([]);
  const [myMissions, setMyMissions] = useState([]);
  const [myTrips, setMyTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    available: 0,
    active: 0,
    completed: 0,
    earnings: 0,
    activeTrips: 0,
  });

  const fetchData = useCallback(async () => {
    try {
      const [availableRes, missionsRes, tripsRes] = await Promise.all([
        parcelsApi.getAvailable(),
        parcelsApi.getAll(),
        tripsApi.getMy()
      ]);
      
      const available = availableRes.data;
      const trips = tripsRes.data;
      const missions = missionsRes.data.filter(p => p.carrier_id === user.id);
      
      setAvailableParcels(available);
      setMyMissions(missions);
      setMyTrips(trips.filter(t => t.status === 'active'));
      
      const completedMissions = missions.filter(m => ['delivered', 'paid'].includes(m.status));
      const totalEarnings = completedMissions.reduce((sum, m) => sum + (m.final_price || m.proposed_price || m.base_price || 0), 0);
      
      setStats({
        available: available.length,
        active: missions.filter(m => ['accepted', 'in_progress'].includes(m.status)).length,
        completed: completedMissions.length,
        earnings: totalEarnings,
        activeTrips: trips.filter(t => t.status === 'active').length,
      });
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAccept = async (parcelId) => {
    try {
      await parcelsApi.accept(parcelId);
      toast.success('Mission acceptée!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'acceptation');
    }
  };

  const statCards = [
    { label: 'Trajets actifs', value: stats.activeTrips, icon: Car, color: 'bg-purple-100 text-purple-600' },
    { label: 'Colis dispo', value: stats.available, icon: Package, color: 'bg-blue-100 text-blue-600' },
    { label: 'Livraisons', value: stats.active, icon: Truck, color: 'bg-yellow-100 text-yellow-600' },
    { label: 'Gains', value: formatPrice(stats.earnings), icon: Euro, color: 'bg-green-100 text-green-600', isPrice: true },
  ];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="carrier-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-900 uppercase tracking-tight">
            Tableau de bord Cotransporteur
          </h1>
          <p className="text-slate-600 mt-1">Publiez vos trajets et transportez des colis</p>
        </div>
        <Link to="/dashboard/new-trip">
          <Button className="bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wide" data-testid="new-trip-btn">
            <Plus className="w-5 h-5 mr-2" />
            Publier un trajet
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="border-slate-200" data-testid={`stat-card-${index}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 uppercase tracking-wide font-medium">{stat.label}</p>
                    <p className={`text-2xl font-bold text-slate-900 mt-1 ${stat.isPrice ? 'text-xl' : ''}`}>
                      {stat.isPrice ? stat.value : stat.value}
                    </p>
                  </div>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/dashboard/new-trip" className="block">
          <Card className="border-slate-200 hover:border-orange-300 hover:shadow-lg transition-all cursor-pointer h-full" data-testid="quick-action-trip">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center">
                <Car className="w-7 h-7 text-orange-500" />
              </div>
              <div>
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-slate-900 uppercase">Publier un trajet</h3>
                <p className="text-sm text-slate-500">Proposez votre prochain voyage</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 ml-auto" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/dashboard/my-trips" className="block">
          <Card className="border-slate-200 hover:border-orange-300 hover:shadow-lg transition-all cursor-pointer h-full" data-testid="quick-action-my-trips">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                <Route className="w-7 h-7 text-purple-500" />
              </div>
              <div>
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-slate-900 uppercase">Mes trajets</h3>
                <p className="text-sm text-slate-500">{stats.activeTrips} trajet{stats.activeTrips > 1 ? 's' : ''} actif{stats.activeTrips > 1 ? 's' : ''}</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 ml-auto" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/dashboard/available" className="block">
          <Card className="border-slate-200 hover:border-orange-300 hover:shadow-lg transition-all cursor-pointer h-full" data-testid="quick-action-parcels">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                <Package className="w-7 h-7 text-blue-500" />
              </div>
              <div>
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-slate-900 uppercase">Colis disponibles</h3>
                <p className="text-sm text-slate-500">{stats.available} colis à transporter</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 ml-auto" />
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* My Active Trips */}
      {myTrips.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase">
                Mes trajets actifs
              </CardTitle>
              <Link to="/dashboard/my-trips" className="text-sm text-orange-500 hover:text-orange-600 font-medium">
                Voir tout
              </Link>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
              {myTrips.slice(0, 3).map((trip) => (
                <Link
                  key={trip.id}
                  to="/dashboard/my-trips"
                  className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Car className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900">
                      {trip.departure_city} → {trip.arrival_city}
                    </p>
                    <p className="text-sm text-slate-500">
                      {formatDate(trip.departure_date)} à {trip.departure_time} • {trip.available_weight}kg dispo
                    </p>
                  </div>
                  <span className="text-lg font-bold text-green-600">{trip.price_per_kg}€/kg</span>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Available Missions */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center justify-between">
            <CardTitle className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase">
              Missions disponibles
            </CardTitle>
            <Link to="/dashboard/available" className="text-sm text-orange-500 hover:text-orange-600 font-medium">
              Voir tout ({stats.available})
            </Link>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="spinner mx-auto"></div>
              <p className="text-slate-500 mt-4">Chargement...</p>
            </div>
          ) : availableParcels.length === 0 ? (
            <div className="p-8 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucune mission disponible pour le moment</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {availableParcels.slice(0, 5).map((parcel) => (
                <div 
                  key={parcel.id} 
                  className="p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`available-parcel-${parcel.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Package className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        <span className="font-medium text-slate-900">
                          {parcel.pickup_address.city}
                        </span>
                        <ArrowRight className="w-4 h-4 text-slate-400" />
                        <span className="font-medium text-slate-900">
                          {parcel.delivery_address.city}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span>{parcel.weight}kg</span>
                        <span>{parcel.distance_km}km</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(parcel.pickup_date)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 mt-1 truncate">
                        {parcel.description || 'Pas de description'}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-lg font-bold text-green-600">
                        {formatPrice(parcel.proposed_price || parcel.base_price)}
                      </p>
                      <p className="text-xs text-slate-500">budget expéditeur</p>
                      <Button 
                        size="sm"
                        className="mt-2 bg-orange-500 hover:bg-orange-600 text-white"
                        onClick={() => handleAccept(parcel.id)}
                        data-testid={`accept-btn-${parcel.id}`}
                      >
                        Accepter
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* My Active Missions */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center justify-between">
            <CardTitle className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase">
              Mes missions en cours
            </CardTitle>
            <Link to="/dashboard/my-missions" className="text-sm text-orange-500 hover:text-orange-600 font-medium">
              Voir tout
            </Link>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {myMissions.filter(m => ['accepted', 'in_progress'].includes(m.status)).length === 0 ? (
            <div className="p-8 text-center">
              <Truck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucune mission en cours</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {myMissions.filter(m => ['accepted', 'in_progress'].includes(m.status)).slice(0, 5).map((mission) => (
                <Link 
                  key={mission.id} 
                  to={`/dashboard/parcel/${mission.id}`}
                  className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`mission-item-${mission.id}`}
                >
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Truck className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900">
                      {mission.pickup_address.city} → {mission.delivery_address.city}
                    </p>
                    <p className="text-sm text-slate-500">
                      Expéditeur: {mission.sender_name}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getStatusColor(mission.status)}`}>
                      {getStatusLabel(mission.status)}
                    </span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400" />
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CarrierDashboard;
